import { useEffect, useState } from "react";
import { Button, Col, ColorPicker, Form, Input, Row, Upload, message } from "antd";
import { DeleteOutlined, UploadOutlined } from "@ant-design/icons";
import { t } from "i18next";
import ApiService from "../../core/service/api-service";
import { loadBranding } from "../../core/service/branding-service";
import "./branding.css";

/**
 * Beyaz etiket marka yönetimi.
 *
 * Görseller DB'de tutulur; dosya sunucusuna kopyalamak, deploy'a eklemek yok.
 * Bir görsel silinince uygulama o alan için bizim gömülü varsayılanımıza döner (kısmi beyaz etiket).
 *
 * SVG kabul edilmez: SVG bir XML belgesidir, içine script gömülebilir ve aynı origin'den servis
 * edilince XSS'e dönüşür. Sunucu içerik tipini uzantıdan değil magic byte'tan doğrular.
 *
 * Metinler HAM tutulur, t() ile render sırasında çevrilir. Burada t() çağırmak sessizce boş
 * başlık üretiyordu: index.js App'i i18n'den önce import ettiği için bu modülün gövdesi
 * i18next.init()'ten önce çalışıyor ve t() undefined dönüyor.
 */
const ASSETS = [
    {
        kind: "HeaderLogo",
        title: 'Header logo',
        hint: 'Sits on the dark (slate) header — use a light or inverted variant.',
        preview: "dark",
        bgField: "headerLogoBackground",
    },
    {
        kind: "LoginLogo",
        title: 'Login screen logo',
        hint: 'Sits inside the login card, on a white background.',
        preview: "light",
        bgField: "loginLogoBackground",
    },
    {
        kind: "LoginBackground",
        title: 'Login screen background',
        hint: 'A photo. A dark overlay is applied on top for readability.',
        preview: "light",
    },
    {
        kind: "Favicon",
        title: "Favicon",
        hint: 'Square PNG (64×64 or 192×192 recommended).',
        preview: "light",
    },
];

/** antd ColorPicker değeri → "#rrggbb" | null (boş = zemin yok) */
const toHex = (value) => {
    if (!value) return null;
    if (typeof value === "string") return value;
    return typeof value.toHexString === "function" ? value.toHexString() : null;
};

export default function BrandingPanel()
{
    const [form] = Form.useForm();
    const [assets, setAssets] = useState({});
    const [backgrounds, setBackgrounds] = useState({});
    const [loading, setLoading] = useState(false);
    // Kaydet ucu tüm alanları birlikte yazar; WhatsApp sekmesi ayrı form olduğu için kayıt son yüklenen
    // ayarların ÜZERİNE yapılır — burada olmayan alanlar (destek numarası vb.) boşalmasın.
    const [settings, setSettings] = useState({});

    const load = async () => {
        const res = await ApiService.Branding.GetSettings();
        if (!res?.success) return;

        setSettings(res.data);

        form.setFieldsValue({
            displayName: res.data.displayName,
            titleTr: res.data.titleTr,
            titleEn: res.data.titleEn,
            descriptionTr: res.data.descriptionTr,
            descriptionEn: res.data.descriptionEn,
            headerLogoBackground: res.data.headerLogoBackground || null,
            loginLogoBackground: res.data.loginLogoBackground || null,
        });
        setAssets(res.data.assets || {});
        setBackgrounds({
            headerLogoBackground: res.data.headerLogoBackground || null,
            loginLogoBackground: res.data.loginLogoBackground || null,
        });
    };

    useEffect(() => {
        load();
    }, []);

    const save = async (values) => {
        setLoading(true);
        const res = await ApiService.Branding.SaveSettings({
            ...settings,
            ...values,
            headerLogoBackground: toHex(values.headerLogoBackground),
            loginLogoBackground: toHex(values.loginLogoBackground),
        });
        setLoading(false);
        if (res?.success) {
            await load();
            await loadBranding();       // başlık/favicon/logo anında güncellensin
        }
    };

    const upload = async (kind, file) => {
        const res = await ApiService.Branding.UploadAsset(kind, file);
        if (res?.success) {
            await load();
            await loadBranding();
        }
        return false;   // antd Upload'un kendi isteğini engelle
    };

    const remove = async (kind) => {
        const res = await ApiService.Branding.RemoveAsset(kind);
        if (res?.success) {
            await load();
            await loadBranding();
            message.info(t("Default branding restored for this asset"));
        }
    };

    const assetUrl = (kind) => {
        const key = kind.charAt(0).toLowerCase() + kind.slice(1);   // HeaderLogo → headerLogo
        const url = assets[key];
        return url ? `${window.AppSettings.server.api}${url}` : null;
    };

    /** Önizleme, gerçek yüzeyi taklit eder: header koyu, giriş kartı beyaz + seçilen zemin kutusu. */
    const previewStyle = (asset) => {
        const bg = asset.bgField ? backgrounds[asset.bgField] : null;
        return bg ? { background: bg } : undefined;
    };

    return (
        <div className="branding-card">
                <p className="branding-intro">
                    {t("Logos and titles are stored in the database. Uploading is enough — no file deployment needed. Removing an asset restores the built-in default.")}
                </p>

                <Form form={form} layout="vertical" onFinish={save}>
                    <Row gutter={[16, 16]}>
                        {ASSETS.map((asset) => (
                            <Col xs={24} md={12} key={asset.kind}>
                                <div className="branding-asset">
                                    <div className="branding-asset-head">
                                        <b>{t(asset.title)}</b>
                                        {assetUrl(asset.kind) && (
                                            <Button
                                                size="small"
                                                type="text"
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={() => remove(asset.kind)}
                                            >
                                                {t("Remove")}
                                            </Button>
                                        )}
                                    </div>

                                    <div className={`branding-preview ${asset.preview === "dark" ? "on-dark" : ""}`}>
                                        {assetUrl(asset.kind)
                                            ? (
                                                <span className="branding-preview-box" style={previewStyle(asset)}>
                                                    <img src={assetUrl(asset.kind)} alt="" />
                                                </span>
                                            )
                                            : <span className="branding-preview-empty">{t("Using default (Kuantum)")}</span>}
                                    </div>

                                    <div className="branding-asset-foot">
                                        <span className="branding-hint">{t(asset.hint)}</span>
                                        <Upload
                                            accept="image/png,image/webp,image/jpeg"
                                            showUploadList={false}
                                            beforeUpload={(file) => upload(asset.kind, file)}
                                        >
                                            <Button icon={<UploadOutlined />}>{t("Upload")}</Button>
                                        </Upload>
                                    </div>

                                    {/* zemin rengi: yalnız logolarda. Şeffaf olmayan logo, seçilen rengin kutusuna alınır. */}
                                    {asset.bgField && (
                                        <div className="branding-asset-bg">
                                            <Form.Item
                                                name={asset.bgField}
                                                label={t("Logo background (leave empty if transparent)")}
                                                className="branding-color"
                                            >
                                                <ColorPicker
                                                    allowClear
                                                    format="hex"
                                                    disabledAlpha
                                                    onChange={(value) =>
                                                        setBackgrounds((prev) => ({ ...prev, [asset.bgField]: toHex(value) }))
                                                    }
                                                    onClear={() =>
                                                        setBackgrounds((prev) => ({ ...prev, [asset.bgField]: null }))
                                                    }
                                                />
                                            </Form.Item>
                                        </div>
                                    )}
                                </div>
                            </Col>
                        ))}
                    </Row>

                    <div className="branding-form">
                        <Row gutter={16}>
                            <Col xs={24} md={12}>
                                <Form.Item label={t("Institution name")} name="displayName">
                                    <Input placeholder={t("Shown in the header/footer")} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col xs={24} md={12}>
                                <Form.Item label={`${t("Browser title")} (TR)`} name="titleTr">
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item label={`${t("Browser title")} (EN)`} name="titleEn">
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col xs={24} md={12}>
                                <Form.Item label={`${t("Description")} (TR)`} name="descriptionTr">
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item label={`${t("Description")} (EN)`} name="descriptionEn">
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Button type="primary" htmlType="submit" loading={loading}>
                            {t("Save")}
                        </Button>
                    </div>
                </Form>
        </div>
    );
}
